import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { AuthModalReason } from "@/lib/player";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, EyeOff, Mail } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { createUserProfile } from "@/lib/profiles";
import { UserCacheService } from "@/lib/user-cache";
import { appEntryFor } from "@/lib/account-setup";
import { buildOnboardingUrlWithReturn, buildSignupUrl } from "@/lib/auth-redirect";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

/** What the modal says. A caller can say what the sign-in is for. */
export interface AuthModalCopy {
  title: string;
  description: string;
  /** The toast once signed in; null shows none, for a caller that reports what happens next. */
  successToast: string | null;
}

/**
 * Neutral on purpose. Listening needs no account (owner decision, 2026-10-13: full-length previews
 * for signed-out visitors), so the modal opens for downloads, licence requests, rights holders and
 * the like, and nothing plays once it closes.
 */
const DEFAULT_AUTH_MODAL_COPY: AuthModalCopy = {
  title: "Sign in",
  description: "Sign in with your account or create a new one.",
  successToast: "You're now signed in.",
};

/**
 * The copy for each reason the player store opens the modal with; `copy` overrides it. "play" is
 * the store's default reason, used by everything that isn't a licence request or a playlist (a
 * download, a storefront search, the preview player's Sign in), so it gets the neutral copy.
 */
const REASON_COPY: Record<AuthModalReason, AuthModalCopy> = {
  play: DEFAULT_AUTH_MODAL_COPY,
  license: {
    title: "Sign in to request a license",
    description:
      "You need to be signed in to request a license. Sign in with your account or create a new one.",
    successToast: "You're now signed in.",
  },
  playlist: {
    title: "Sign in to save to a playlist",
    description:
      "You need to be signed in to add songs to your playlists. Sign in with your account or create a new one.",
    successToast: "You're now signed in.",
  },
};

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthSuccess?: () => void;
  /** What the sign-in is for: requesting a license, saving to a playlist, or anything else ("play", the default). */
  reason?: AuthModalReason;
  /** The caller's own copy, over the reason's. */
  copy?: AuthModalCopy;
}

/**
 * The modal only ever creates buyer accounts (owner decision, 2026-10-30). It opens on public
 * pages — a shared track, a shared playlist, a storefront — whose visitors want to license, and a
 * seller made here hit a dead end: seller onboarding needs a label invite code. Rights holders sign
 * up through the full page (`/auth/signup?side=seller`), which the sign-up tab links to.
 */
const MODAL_SIGNUP_ROLE = "buyer";

const glassDialogClass =
  "border border-white/[0.08] bg-card/60 backdrop-blur-xl text-card-foreground shadow-[0_8px_32px_rgba(0,0,0,0.4),0_2px_8px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.05)]";

const getFirstPasswordIssue = (password: string): string | null => {
  if (password.length < 8)
    return "Password must be at least 8 characters long.";
  if (!/[A-Z]/.test(password))
    return "Add at least one uppercase letter (A-Z).";
  if (!/[a-z]/.test(password))
    return "Add at least one lowercase letter (a-z).";
  if (!/[0-9]/.test(password)) return "Add at least one number (0-9).";
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
    return "Add at least one special character (!@#$...).";
  return null;
};

export function AuthModal({
  open,
  onOpenChange,
  onAuthSuccess,
  reason = "play",
  copy: callerCopy,
}: AuthModalProps) {
  const copy = callerCopy ?? REASON_COPY[reason];
  const [activeTab, setActiveTab] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}`;
  // The page that opened the modal may act on the same sign-in first (the shared track page sends
  // an unfinished account to onboarding itself) and unmount it; a modal that is gone must not
  // navigate again, or onboarding lands in the history twice.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const passwordIssue = getFirstPasswordIssue(password);
  const isPasswordValid = !passwordIssue;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setEmail("");
      setPassword("");
      setError(null);
      setSuccessMessage(null);
      setEmailConfirmationSent(false);
      setConfirmationCode("");
      setShowPassword(false);
      setActiveTab("login");
    }
  }, [open]);

  // Check if user is authenticated after successful login/signup
  useEffect(() => {
    if (user && open) {
      // Small delay to ensure state is updated. Cancelled if the caller closes the modal first: it
      // has taken the sign-in over itself (the shared track page does, for a licence request).
      const timer = setTimeout(() => {
        handleAuthSuccess();
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open]);

  // Listen for email confirmation
  useEffect(() => {
    if (!emailConfirmationSent) return;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === "SIGNED_IN") {
        const {
          data: { user: currentUser },
        } = await supabase.auth.getUser();
        if (currentUser?.email === email) {
          setEmailConfirmationSent(false);
          // This listener only ever fires while emailConfirmationSent is true, which is only
          // ever set by handleSignup — so this SIGNED_IN is always completing a signup, never
          handleAuthSuccess();
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [emailConfirmationSent, email]);

  const handleAuthSuccess = async () => {
    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      if (currentUser) {
        // An account that is not set up (a sign-up made here, or a sign-in to one never finished)
        // goes to onboarding and comes back to this page, instead of the caller's action: an
        // unfinished account may not download, see rights holders or save a playlist. Before
        // this, the modal just closed and left new buyers on the public page with no next step.
        // A licence request is the exception: a draft buyer may file its first one before
        // onboarding (2026-10-14), and sending it takes the buyer through onboarding.
        // A profile that cannot be read fails closed, as everywhere `appEntryFor` is asked.
        const profile = await UserCacheService.getProfile(currentUser.id).catch(() => null);
        if (!mountedRef.current) return;
        if (reason !== "license" && appEntryFor(profile) === "onboarding") {
          onOpenChange(false);
          navigate(buildOnboardingUrlWithReturn(currentPath));
          return;
        }
        if (copy.successToast) {
          toast({ title: "Success!", description: copy.successToast });
        }
        onAuthSuccess?.();
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Error finishing sign-in:", error);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    if (!email || !password) {
      setError("Please enter both email and password");
      setLoading(false);
      return;
    }

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword(
        {
          email,
          password,
        }
      );

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      if (!data?.user || !data?.session) {
        setError("Login failed. Please try again.");
        setLoading(false);
        return;
      }

      // Success - will be handled by useEffect watching user state
      setSuccessMessage("Login successful!");
    } catch (err) {
      console.error("Login error:", err);
      setError("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!isPasswordValid) {
      setError(passwordIssue || "Password is invalid.");
      return;
    }

    setLoading(true);

    try {
      // Preserve current page URL so email confirmation redirects back here
      const emailRedirectTo = `${window.location.origin}/?redirect=${encodeURIComponent(currentPath)}`;

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { role: MODAL_SIGNUP_ROLE }, emailRedirectTo },
      });

      if (authError) {
        if (authError.message.includes("User already registered")) {
          setError("This email is already registered. Please log in instead.");
          setActiveTab("login");
        } else {
          setError(authError.message);
        }
        setLoading(false);
        return;
      }

      // If signup was successful and we have a session immediately, create profile
      if (authData.session && authData.user) {
        await createUserProfile(authData.user.id, authData.user.email!);

        setSuccessMessage("Sign up successful!");
        // Success - will be handled by useEffect watching user state
      } else {
        // Email confirmation required
        setEmailConfirmationSent(true);
        setSuccessMessage(
          "Confirmation email sent! Please check your inbox and enter the confirmation code below."
        );
      }
    } catch (err) {
      console.error("Signup error:", err);
      setError("An unexpected error occurred.");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyingCode(true);
    setError(null);

    try {
      // Verify the code by attempting to sign in
      // Supabase will automatically verify the email when the user clicks the link
      // But we can also check if the user is now authenticated
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (currentUser?.email_confirmed_at) {
        // User is already confirmed
        await handleAuthSuccess();
      } else {
        setError(
          "Please click the confirmation link in your email, then try again."
        );
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError("Verification failed. Please check your email and try again.");
    } finally {
      setVerifyingCode(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("sm:max-w-[500px]", glassDialogClass)}>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        {emailConfirmationSent ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>We've sent a confirmation email to {email}</span>
            </div>

            {successMessage && (
              <div role="status" className="bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 px-4 py-2 rounded-md text-sm">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <Label htmlFor="confirmationCode">
                  Confirmation Code (Optional)
                </Label>
                <Input
                  id="confirmationCode"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  spellCheck={false}
                  placeholder="Enter code from email (or click link in email)"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                />
                <p className="text-[13px] text-muted-foreground mt-1">
                  You can also just click the link in your email - we'll detect
                  when you're confirmed.
                </p>
              </div>

              {error && (
                <div role="alert" className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setEmailConfirmationSent(false);
                    setConfirmationCode("");
                  }}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={verifyingCode}
                  className="flex-1"
                >
                  {verifyingCode ? "Verifying…" : "Verify & Continue"}
                </Button>
              </div>
            </form>

            <div className="text-[13px] text-muted-foreground text-center">
              Didn't receive the email? Check your spam folder or{" "}
              <button
                type="button"
                onClick={() => {
                  supabase.auth.resend({
                    type: "signup",
                    email,
                  });
                  toast({
                    title: "Email resent",
                    description: "Please check your inbox.",
                  });
                }}
                className="text-primary hover:underline"
              >
                resend it
              </button>
              .
            </div>
          </div>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "login" | "signup")}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    spellCheck={false}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div role="alert" className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div role="status" className="bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 px-4 py-2 rounded-md text-sm">
                    {successMessage}
                  </div>
                )}

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    spellCheck={false}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={
                      password.length > 0
                        ? isPasswordValid
                          ? "border-green-500"
                          : "border-red-500"
                        : ""
                    }
                  />
                  {password.length > 0 && (
                    <p
                      className={`text-sm ${
                        isPasswordValid ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {isPasswordValid ? "Strong password" : passwordIssue}
                    </p>
                  )}
                </div>

                {error && (
                  <div role="alert" className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div role="status" className="bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 px-4 py-2 rounded-md text-sm">
                    {successMessage}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !isPasswordValid}
                  className="w-full"
                >
                  {loading ? "Signing up…" : "Sign Up"}
                </Button>

                <p className="text-center text-[13px] text-muted-foreground">
                  Representing a label or publisher?{" "}
                  <Link
                    to={buildSignupUrl({ side: "seller", redirectPath: currentPath })}
                    onClick={() => onOpenChange(false)}
                    className="text-primary hover:underline"
                  >
                    Sign up as a rights holder
                  </Link>
                </p>
              </form>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
